/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */

import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Backdrop,
  Button,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  ListSubheader,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material';
import MuiAppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import { styled } from '@mui/material/styles';
import { AppBarProps, SharedStateContextType } from "@propTypes/index";
import { Auth } from "aws-amplify";
import { AppBarStyle } from 'customStyles/index';
import FocusTrap from 'focus-trap-react';
import React, { createContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUserStore } from "state";
import { useAuth } from "../../hooks";
import { Mode, useMUITheme } from "../../theme/ThemeProvider";
import Shortcuts from "./components/Shortcuts";
import UserProfile from "./components/UserProfile";

const SharedStateContext = createContext<SharedStateContextType | undefined>(undefined)

const SharedStateProvider = ({ children }) => {
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [displayShortcut, setDisplayShortcut] = useState<boolean>(false)
  return (
    <SharedStateContext.Provider value={{ isVisible, setIsVisible, displayShortcut, setDisplayShortcut }}>
      {children}
    </SharedStateContext.Provider>
  )
}








const drawerWidth = 256;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export const Appbar: React.FC<AppBarProps> = ({ open, handleDrawerOpen, handleDrawerClose }) => {
  const [openBackDrop, setOpenBackDrop] = useState(false)
  const [shortCuts, setOpenShortCuts] = useState(false)
  const [userProfile, setOpenUserProfile] = useState(false)
  const [displaySettings, setOpenDisplaySettings] = useState(false)
  const [menuWidth, setMenuWidth] = useState("0px")
  const [menuHeight, setMenuHeight] = useState("0px")
  const { mode, toggleTheme, saveTheme } = useMUITheme();
  console.log(`debug group call running useUserCognito from AppBar`);

  const STATE = process.env.REACT_APP_CONFIG_STATE!;

  const { logout } = useAuth();
  const userConfig = useUserStore(state => state.userConfig)
  const { userGivenName, userFamilyName, userRole } = userConfig;
  const [userInitials, setUserInitials] = useState("")

  const location = useLocation();
  const applicantDetails = location.pathname.includes('applicant-details')
  const profileRef = useRef<HTMLDivElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (shortCuts) {
      setMenuWidth("384px")
      setMenuHeight("413px")
    }

    if (userProfile) {
      setMenuWidth(`220px`);
      setMenuHeight("auto");
    }

  }, [shortCuts, userProfile])

  useEffect(() => {
    if (userGivenName?.length > 0 && userFamilyName?.length > 0) {
      setUserInitials(userGivenName?.[0].toUpperCase() + userFamilyName?.[0].toUpperCase())
    } else if (userGivenName?.length > 0 && userFamilyName?.length < 1) {
      if (userGivenName?.length > 1) {
        setUserInitials(userGivenName?.[0].toUpperCase() + userGivenName?.[1].toUpperCase())
      } else {
        setUserInitials(userGivenName?.toUpperCase())
      }
    }
  }, [userGivenName, userFamilyName])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setOpenUserProfile(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  const handleLogout = () => {
    if (logout) logout();
  };

  const openShortCuts = () => {
    setOpenShortCuts(!shortCuts)
    setOpenUserProfile(false)
  }

  const openUserProfile = () => {
    setOpenUserProfile(!userProfile)
    setOpenShortCuts(false)
  }

  const closeUserSettings = () => {
    setOpenUserProfile(false)
  }

  const handleBackDropClose = () => {
    setOpenBackDrop(false)
  }

  const handleOpenDisplaySettings = () => {
    setOpenBackDrop(true)
    setOpenDisplaySettings(true)
  }

  const handleCloseDisplaySettings = () => {
    setOpenBackDrop(false)
    setOpenDisplaySettings(false)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleCloseDisplaySettings()
      openUserProfile()
    } else if (event.key === 'Escape') {
      closeUserSettings()
    }
  };

  const handleThemeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newThemeValue = event.target.value as 'light' | 'dark';
    const newTheme = newThemeValue === 'dark' ? Mode.DARK : Mode.LIGHT;
    const { username } = await Auth.currentAuthenticatedUser();

    if (saveTheme) {
      saveTheme(newTheme, username);
    }
  }

  return (
    <SharedStateProvider>
      <>
        <AppBar position="fixed" enableColorOnDark color='secondary' open={open}>
          <Toolbar disableGutters sx={{ width: "100%", justifyContent: "space-around", gap: "16px" }}>
            <Container maxWidth={false} disableGutters sx={AppBarStyle.navInnerContainerStyle}>
              {!applicantDetails
                &&
                <Tooltip title={open ? "Close Drawer" : "Open Drawer"}>
                  <IconButton id="drawer-button" data-testid="drawer-menu-button" role="button" aria-label={!open ? "Open drawer" : "Close drawer"} onClick={open ? () => handleDrawerClose?.() : () => handleDrawerOpen?.()} color="inherit" sx={{ ...AppBarStyle.iconContainerStyle, marginRight: "32px" }}>
                    <MenuIcon fontSize="inherit" aria-label="Menu" />
                  </IconButton>
                </Tooltip>
              }
              <Typography sx={{ ...AppBarStyle.titleStyle, pl: applicantDetails ? '0.8rem' : '0.3rem' }} data-testid="app-bar-cares">
                {process.env.REACT_APP_TITLE || 'CARES'}
              </Typography>
            </Container>
            <Stack spacing="1" direction="row" sx={{ pr: '16px', gap: 1, height: "40px" }}>

              <Tooltip title="Open Help">
                <IconButton color="inherit" sx={AppBarStyle.iconContainerStyle} onClick={() => window.open("/user-guide", "_blank")} data-testid="open-help-icon-button" aria-label='Help'>
                  <HelpOutlineIcon fontSize="inherit" sx={{ color: '#fff' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open User Settings">
                <Avatar onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} tabIndex={0} onClick={() => openUserProfile()} onKeyDown={handleKeyDown}
                  role="button" aria-label="Open user settings"
                  alt="User's Avatar" variant="circular" ref={profileRef} sx={{ border: isFocused ? '2px solid #005fcc' : 'none', backgroundColor: '#64646B', '&:hover': { cursor: 'pointer' } }} data-testid="open-user-setting-avatar">
                  <Typography variant="body1" color='#fff'>{userInitials}</Typography>
                </Avatar>
              </Tooltip>
            </Stack>
          </Toolbar>
        </AppBar>
        <Paper sx={{
          ...AppBarStyle.appBarMenuStyle,
          display: shortCuts || userProfile ? "flex" : "none",
          width: menuWidth,
          height: menuHeight
        }}>

          {
            shortCuts ?
              <Shortcuts /> :
              userProfile ?
                <UserProfile
                  handleOpenDisplaySettings={handleOpenDisplaySettings}
                  handleLogout={handleLogout}
                  closeUserSettings={closeUserSettings}
                  userRole={userRole}
                  userGivenName={userGivenName}
                  userFamilyName={userFamilyName}
                  userInitials={userInitials}
                /> : null
          }

        </Paper>
        {
          displaySettings && openBackDrop

            ? <>
              <Backdrop open={openBackDrop} onClick={handleBackDropClose} sx={AppBarStyle.backDropStyle} />
              <FocusTrap>
                <Container disableGutters sx={AppBarStyle.backDropContainer}>
                  <Container maxWidth={false} disableGutters sx={AppBarStyle.backdropButtonContainer}>
                    <Button
                      onKeyDown={handleKeyDown}
                      onClick={handleCloseDisplaySettings}
                      tabIndex={0}
                      variant="text" size='large' startIcon={<CloseIcon sx={AppBarStyle.closeIcon} />} >
                      <Typography sx={AppBarStyle.closeButtonTextStyle} color='text.primary'>
                        Close
                      </Typography>
                    </Button>
                  </Container>
                  <FormControl sx={{ marginTop: "1rem" }}>
                    <FormLabel sx={AppBarStyle.formLabelStyle} id="theme-radio-buttons-group-label" >
                      <Typography variant="h1" color='text.primary' fontSize="1.5rem" fontWeight="400">
                        Display Settings
                      </Typography>
                    </FormLabel>
                    <Divider sx={{ marginBottom: "15px" }} aria-hidden role="separator" />
                    <ListSubheader sx={AppBarStyle.listSubHeaderStyle}>
                      COLOR SCHEME
                    </ListSubheader>
                    <RadioGroup
                      aria-label="Dark/Light Mode Select"
                      value={mode}
                      name="radio-buttons-group"
                      sx={{ fontSize: '1rem', ml: '0.4rem' }}
                      onChange={handleThemeChange}
                    >
                      <FormControlLabel value="dark" control={<Radio />} label="Dark Mode" />
                      <FormControlLabel value="light" control={<Radio />} label="Light Mode" />
                    </RadioGroup>
                  </FormControl>
                </Container>
              </FocusTrap>
            </>
            : null
        }
      </>
    </SharedStateProvider>
  );
};
